import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  FileText, Plus, Loader2, Trash2, Share2, ExternalLink, Copy,
  Megaphone, Eye,
} from 'lucide-react';
import SEAReportPdfPreview from '../../components/sea/SEAReportPdfPreview';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR');
}

export default function SEAReports() {
  const [reports, setReports] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [previewReport, setPreviewReport] = useState(null);
  const [shareReport, setShareReport] = useState(null);

  const reload = useCallback(async () => {
    try {
      const [r, c] = await Promise.all([
        axios.get(`${API}/sea-reports`, { withCredentials: true }),
        axios.get(`${API}/clients`, { withCredentials: true }),
      ]);
      setReports(r.data || []);
      setClients((c.data || []).filter(cl => cl.google_ads_customer_id && cl.sea_active !== false));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const deleteReport = async (id) => {
    if (!window.confirm('Supprimer ce rapport ?')) return;
    try {
      await axios.delete(`${API}/sea-reports/${id}`, { withCredentials: true });
      toast.success('Rapport supprimé');
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="sea-reports-page">
        <header className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-heading font-bold text-neutral-900 flex items-center gap-2">
              <Megaphone className="w-7 h-7 text-orange-600" />
              Rapports SEA
            </h1>
            <p className="text-neutral-600 text-sm mt-1">
              Rapports Google Ads avec lien de partage public et export PDF — closed-loop ROI inclus.
            </p>
          </div>
          <Button onClick={() => setCreateOpen(true)} className="bg-[#002FA7] hover:bg-[#002277]"
                  disabled={clients.length === 0} data-testid="sea-report-new-btn">
            <Plus className="w-4 h-4 mr-1" /> Nouveau rapport
          </Button>
        </header>

        {clients.length === 0 && !loading && (
          <div className="bg-amber-50 border border-amber-200 rounded p-4 text-sm text-amber-900">
            Aucun client lié à Google Ads. <Link to="/sea/dashboard" className="underline">Liez un client</Link> pour pouvoir générer un rapport SEA.
          </div>
        )}

        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#002FA7] mx-auto" /></div>
        ) : reports.length === 0 ? (
          <div className="bg-white border border-dashed border-neutral-300 rounded-lg p-10 text-center text-neutral-500">
            <FileText className="w-10 h-10 mx-auto text-neutral-300 mb-2" />
            Aucun rapport SEA pour l'instant. Cliquez sur "Nouveau rapport" pour commencer.
          </div>
        ) : (
          <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-xs text-neutral-500 uppercase">
                <tr>
                  <th className="text-left px-4 py-2.5">Titre</th>
                  <th className="text-left px-4 py-2.5">Client</th>
                  <th className="text-left px-4 py-2.5">Période</th>
                  <th className="text-left px-4 py-2.5">Statut</th>
                  <th className="text-left px-4 py-2.5">Créé</th>
                  <th className="text-right px-4 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {reports.map(r => (
                  <tr key={r.id} className="hover:bg-neutral-50" data-testid={`sea-report-row-${r.id}`}>
                    <td className="px-4 py-3 font-medium">{r.title}</td>
                    <td className="px-4 py-3 text-neutral-700">{r.client_name || '—'}</td>
                    <td className="px-4 py-3 text-neutral-600 text-xs">{r.period_start} → {r.period_end}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium uppercase ${
                        r.status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-100 text-neutral-600'
                      }`}>{r.status}</span>
                    </td>
                    <td className="px-4 py-3 text-neutral-500 text-xs">{fmtDate(r.created_at)}</td>
                    <td className="px-4 py-3 text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => setPreviewReport(r)} data-testid={`sea-report-preview-${r.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setShareReport(r)} data-testid={`sea-report-share-${r.id}`}>
                        <Share2 className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteReport(r.id)} className="text-red-600 hover:bg-red-50" data-testid={`sea-report-delete-${r.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <CreateDialog open={createOpen} onOpenChange={setCreateOpen} clients={clients}
          onCreated={(r) => { setCreateOpen(false); reload(); setPreviewReport(r); }} />
        {previewReport && <PreviewDialog report={previewReport} onClose={() => setPreviewReport(null)} />}
        {shareReport && <ShareDialog report={shareReport} onClose={() => setShareReport(null)} />}
      </div>
    </Layout>
  );
}


function CreateDialog({ open, onOpenChange, clients, onCreated }) {
  const today = new Date().toISOString().slice(0, 10);
  const lastMonth = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);

  const [clientId, setClientId] = useState('');
  const [title, setTitle] = useState('');
  const [periodStart, setPeriodStart] = useState(lastMonth);
  const [periodEnd, setPeriodEnd] = useState(today);
  const [summary, setSummary] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (open) {
      setClientId(''); setTitle(''); setSummary('');
      setPeriodStart(lastMonth); setPeriodEnd(today);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async () => {
    if (!clientId) { toast.error('Sélectionnez un client'); return; }
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/sea-reports`, {
        client_id: clientId,
        title: title || `Rapport SEA — ${periodStart} au ${periodEnd}`,
        period_start: periodStart, period_end: periodEnd, summary,
      }, { withCredentials: true });
      toast.success('Rapport créé');
      onCreated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="sea-report-create-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-[#002FA7]" /> Nouveau rapport SEA</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] uppercase font-medium text-neutral-600">Client</label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger data-testid="sea-report-client-select"><SelectValue placeholder="Choisir un client lié à Google Ads" /></SelectTrigger>
              <SelectContent>
                {clients.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase font-medium text-neutral-600">Titre (facultatif)</label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Ex. Rapport Avril 2026" data-testid="sea-report-title" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-[11px] uppercase font-medium text-neutral-600">Début</label>
              <Input type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} data-testid="sea-report-start" /></div>
            <div><label className="text-[11px] uppercase font-medium text-neutral-600">Fin</label>
              <Input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} data-testid="sea-report-end" /></div>
          </div>
          <div>
            <label className="text-[11px] uppercase font-medium text-neutral-600">Synthèse (facultatif)</label>
            <Textarea value={summary} onChange={e => setSummary(e.target.value)} rows={3} placeholder="Commentaires, recommandations…" data-testid="sea-report-summary" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={submit} disabled={creating} className="bg-[#002FA7] hover:bg-[#002277]" data-testid="sea-report-create-submit">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Créer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function PreviewDialog({ report, onClose }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="sea-report-preview-dialog">
        <DialogHeader><DialogTitle>{report.title}</DialogTitle></DialogHeader>
        <SEAReportPdfPreview reportId={report.id} />
      </DialogContent>
    </Dialog>
  );
}


function ShareDialog({ report, onClose }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [expiresDays, setExpiresDays] = useState('30');

  const reload = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/sea-reports/${report.id}/share-links`, { withCredentials: true });
      setLinks(data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setLoading(false); }
  }, [report.id]);

  useEffect(() => { reload(); }, [reload]);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/sea-reports/${report.id}/share`,
        { expires_days: expiresDays ? parseInt(expiresDays) : null },
        { withCredentials: true });
      try { await navigator.clipboard.writeText(data.share_url); } catch (clipErr) {
        console.warn('Clipboard write failed (non-blocking):', clipErr);
      }
      toast.success('Lien créé et copié');
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setCreating(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer ce lien ?')) return;
    try {
      await axios.delete(`${API}/sea-reports/share-links/${id}`, { withCredentials: true });
      toast.success('Lien supprimé'); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="sea-report-share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Share2 className="w-5 h-5 text-[#002FA7]" /> Partager le rapport</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="text-[11px] uppercase font-medium text-neutral-600">Expire dans (jours, vide = jamais)</label>
              <Input value={expiresDays} onChange={e => setExpiresDays(e.target.value.replace(/[^0-9]/g, ''))} placeholder="30" data-testid="sea-share-expires" />
            </div>
            <Button onClick={create} disabled={creating} className="bg-[#002FA7] hover:bg-[#002277]" data-testid="sea-share-create">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Créer un lien</>}
            </Button>
          </div>

          {loading ? (<Loader2 className="w-5 h-5 animate-spin mx-auto text-neutral-400" />) :
           links.length === 0 ? (<div className="text-xs text-neutral-500 text-center py-4">Aucun lien actif</div>) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {links.map(l => (
                <div key={l.id} className="bg-neutral-50 border border-neutral-200 rounded p-2 flex items-center gap-2" data-testid={`sea-share-link-${l.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-mono truncate text-neutral-700">{l.share_url}</div>
                    <div className="text-[10px] text-neutral-400">Créé {fmtDate(l.created_at)}{l.expires_at ? ` · expire ${fmtDate(l.expires_at)}` : ''}</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(l.share_url); toast.success('Copié'); }}>
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                  <a href={l.share_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center h-8 w-8 rounded hover:bg-neutral-100">
                    <ExternalLink className="w-3.5 h-3.5 text-neutral-600" />
                  </a>
                  <Button size="sm" variant="ghost" onClick={() => remove(l.id)} className="text-red-600 hover:bg-red-50">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
